import type { IconName } from '../components/icons.generated';
import type { ImageName } from '../generated/images';

// NOTE: placeholder content from the Stitch design ("About the Atelier"). Names, roles,
// credentials, years and figures below must be confirmed by the client before launch.

export type Principle = { number: string; icon: IconName; title: string; text: string; tag: string };

export const principles: Principle[] = [
  {
    number: '01',
    icon: 'texture',
    title: 'Material Authenticity',
    text: 'We employ honest stone, unlacquered bronze, patinated leathers, and sustainably harvested FSC hardwoods that age with nobility and tactile grace.',
    tag: 'Zero Synthetic Veneers',
  },
  {
    number: '02',
    icon: 'wb_sunny',
    title: 'Light as Sculpture',
    text: 'Harnessing natural dawn-to-dusk solar orientations. Apertures and shadowlines are deliberately shaped to breathe dynamic kinetic spirit into structural stillness.',
    tag: 'Circadian Infiltration',
  },
  {
    number: '03',
    icon: 'spa',
    title: 'Ergonomic Serenity',
    text: 'Seamless spatial flow, concealed service pockets, and intuitive programmatic adjacencies produce acoustic stillness and unhindered sensory calm.',
    tag: 'Effortless Utility',
  },
  {
    number: '04',
    icon: 'verified_user',
    title: 'Turnkey Accountability',
    text: 'Exacting engineering precision, total procurement transparency, and military-grade scheduling ensure zero cost surprises or diluted expectations.',
    tag: 'Complete Ownership',
  },
];

export type Metric = { value: string; suffix?: string; label: string; text: string };

export const metrics: Metric[] = [
  {
    value: '150',
    suffix: '+',
    label: 'Completed Sanctuaries',
    text: 'Spanning private villas, beachfront estates, and discreet boutique hospitality suites.',
  },
  {
    value: '12',
    suffix: '+',
    label: 'Years of Atelier Craft',
    text: 'Continuous architectural leadership without compromise in material or structural standards.',
  },
  {
    value: '98',
    suffix: '%',
    label: 'Client Retention',
    text: 'Patrons who return to entrust our atelier with second residences and estate additions.',
  },
  {
    value: '15',
    label: 'Industry Honors',
    text: 'Accolades celebrating sustainable spatial luxury, joinery excellence, and regional integration.',
  },
];

export type StudioStat = { value: string; label: string };

export const studioStats: StudioStat[] = [
  { value: '08+', label: 'Master Crafts guilds Under Studio Patronage' },
  { value: '100%', label: 'Bespoke Millwork Sourced & Engineered In-House' },
];

export type Principal = {
  name: string;
  role: string;
  subRole: string;
  credentials: string;
  quote: string;
  bio: string;
  specialisms: string[];
  image: ImageName;
  imageAlt: string;
};

export const principal: Principal = {
  name: 'Ar. V. Anandasundaram',
  role: 'Founder & Principal Architect',
  subRole: 'Director of Spatial Strategy',
  credentials: 'B.Arch, M.Sc (Milan) · IIA Member',
  quote:
    'We do not decorate rooms; we sculpt the psychic boundaries within which families converse, heal, celebrate, and contemplate their legacies.',
  bio: "With over 18 years of cross-continental practice spanning Milan, Mumbai, and South India, Anandasundaram founded Green Valley to synthesize pure minimalist silhouettes with South India's indigenous stone and timber heritage. Under his direction, the studio has orchestrated private compounds for industrialists, coastal sanctuaries, and landmark commercial centers.",
  specialisms: ['Master Space Planning', 'Biophilic Light Studies', 'Heritage Granite Masonry'],
  image: 'team-principal-architect',
  imageAlt: 'Portrait of the principal architect of Green Valley Interiors in the studio, surrounded by stone models and drawings',
};

export type TeamMember = {
  name: string;
  role: string;
  text: string;
  experience: string;
  image: ImageName;
  imageAlt: string;
};

export const team: TeamMember[] = [
  {
    name: 'Meera Krishnan',
    role: 'Head of Interior Architecture',
    text: 'Directs volumetric layout curation, luxury FF&E procurement, and bespoke textile sourcing from European and local weaving guilds.',
    experience: '10+ Yrs Architectural Practice',
    image: 'team-interior-architect',
    imageAlt: 'Interior architect holding leather and travertine material swatches in a bright studio',
  },
  {
    name: 'J. Thangadurai',
    role: 'Senior Joinery & Millwork Engineer',
    text: 'Oversees our specialized Nagercoil carpentry foundry, calculating timber tolerances, hidden flush doors, and architectural cabinetry.',
    experience: '22+ Yrs Master Carpentry',
    image: 'team-master-woodworker',
    imageAlt: 'Master woodworker inspecting mortise and tenon joinery on an unvarnished teak bench',
  },
  {
    name: 'S. R. Praveen',
    role: 'Director of Turnkey Delivery',
    text: 'Enforces budget integrity, structural safety audits, contractor alignments, and timeline accuracy for zero-variance completion.',
    experience: '14+ Yrs High-Value Delivery',
    image: 'team-project-director',
    imageAlt: 'Project director reviewing architectural drawings on a tablet at a building site',
  },
];

export type WorkshopCard = { badge: string; title: string; text: string; image: ImageName; imageAlt: string };

export const workshop: WorkshopCard[] = [
  {
    badge: 'Curated Swatch Study',
    title: 'Tactile Calibration',
    text: 'Every client engagement begins in our tactile lab, contrasting surface temperatures, acoustic absorption values, and light reflexivity ratings.',
    image: 'about-material-palette',
    imageAlt: 'Material palette of fluted travertine, brushed brass, raw silk, green granite and walnut veneer samples',
  },
  {
    badge: 'Quarry Selection',
    title: 'Direct Vein Sourcing',
    text: 'We inspect whole slabs at the source in Madurai and Rajasthan quarries, securing continuous book-matched veining for architectural wall installations.',
    image: 'about-stone-quarry',
    imageAlt: 'Sliced granite and limestone blocks inside a stone quarry lit by natural light',
  },
  {
    badge: 'Nagercoil Atelier Works',
    title: 'Precision Millwork',
    text: 'Our bespoke joinery works execute micron-accurate tolerances, pairing Japanese-inspired hidden joinery with climate-cured teak and rosewood.',
    image: 'about-artisan-credenza',
    imageAlt: 'Craftsman smoothing a curved solid teak credenza with a hand plane in a sunlit workshop',
  },
];
